import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

export interface FollowUpRecord {
  id: number;
  profile_id: string;
  action: string;
  note: string | null;
  assignee: string | null;
  actor_id: string | null;
  actor_name: string | null;
  created_at: string;
}

interface FollowUpHistoryResponse {
  history?: FollowUpRecord[];
  error?: string;
}

export const FOLLOW_UP_ACTION_META: Record<string, { label: string; tone: "primary" | "accent" | "secondary" }> = {
  add: { label: "加入跟进清单", tone: "primary" },
  update: { label: "更新跟进备注", tone: "secondary" },
  remove: { label: "移出跟进清单", tone: "accent" },
};

export function followUpActionMeta(action: string) {
  return FOLLOW_UP_ACTION_META[action] ?? { label: action, tone: "secondary" as const };
}

interface UseFollowUpHistoryResult {
  history: FollowUpRecord[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useFollowUpHistory(profileId: string | null, enabled: boolean): UseFollowUpHistoryResult {
  const [history, setHistory] = useState<FollowUpRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!profileId) {
      setHistory([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke("admin-license", {
        body: { action: "followUpHistory", userId: profileId },
      });
      if (invokeError) throw invokeError;
      const result = data as FollowUpHistoryResponse | null;
      if (result?.error) throw new Error(result.error);
      setHistory(result?.history ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "跟进记录加载失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  }, [profileId]);

  useEffect(() => {
    if (enabled) void load();
  }, [enabled, load]);

  return { history, loading, error, reload: load };
}